// AGC: useNotifications - Shared hook for notification management
// AGC: Provides notifications data and unread count that syncs across all components
// AGC: Used by MainMenu (for badge count) and Notifications page (for display)

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from './useAuth'
import { getNotifications, updateNotification, markAllNotificationsAsRead, deleteNotification, deleteAllNotifications } from '../services/notifications'
import { getNotificationStats } from '../data/notifications'
import type { Notification } from '../types'

export interface UseNotificationsReturn {
  notifications: Notification[]
  isLoading: boolean
  error: Error | null
  unreadCount: number          // AGC: Unread system notifications only (for bell badge)
  activityCount: number        // AGC: Total user activities
  notificationCount: number    // AGC: Total system notifications (non-activities)
  stats: ReturnType<typeof getNotificationStats>
  refreshNotifications: () => Promise<void>
  markAsRead: (id: string) => Promise<void>
  markAsUnread: (id: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  deleteNotificationById: (id: string) => Promise<void>
  deleteAll: () => Promise<void>
}

/**
 * AGC: useNotifications - Shared hook for notification management
 * AGC: Loads notifications from Firebase and provides synchronized state
 * AGC: Unread count is calculated from actual notifications data
 * AGC: All components using this hook will have the same notification state
 */
export const useNotifications = (): UseNotificationsReturn => {
  const { getAuthToken } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // AGC: Load notifications from Firebase
  const loadNotifications = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const token = await getAuthToken()
      const data = await getNotifications(token)
      setNotifications(data)
    } catch (err) {
      console.error('AGC: Error loading notifications:', err)
      setError(err instanceof Error ? err : new Error('Failed to load notifications'))
      setNotifications([]) // AGC: New accounts start with empty data
    } finally {
      setIsLoading(false)
    }
  }, [getAuthToken])

  // AGC: Initial load
  useEffect(() => {
    loadNotifications()
  }, [loadNotifications])

  // AGC: Calculate statistics from notifications
  const stats = useMemo(() => getNotificationStats(notifications), [notifications])

  // AGC: CRITICAL - Unread count ONLY for actual notifications (not activities)
  // AGC: Activities (isActivity=true) should NOT show in the bell badge
  // AGC: Bell badge only shows unread system notifications (alerts, reminders, etc.)
  const unreadCount = useMemo(() => {
    return notifications.filter(n => !n.read && !n.isActivity).length
  }, [notifications])
  
  // AGC: Total activity count (for activity log display)
  const activityCount = useMemo(() => {
    return notifications.filter(n => n.isActivity).length
  }, [notifications])
  
  // AGC: Total notification count (non-activities)
  const notificationCount = useMemo(() => {
    return notifications.filter(n => !n.isActivity).length
  }, [notifications])

  // AGC: Refresh notifications (public method)
  const refreshNotifications = useCallback(async () => {
    await loadNotifications()
  }, [loadNotifications])

  // AGC: Mark notification as read
  const markAsRead = useCallback(async (id: string) => {
    try {
      const token = await getAuthToken()
      await updateNotification(id, { read: true }, token)
      
      // AGC: Update local state immediately for instant UI feedback
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      )
    } catch (err) {
      console.error('AGC: Error marking notification as read:', err)
      // AGC: Refresh on error to sync with server
      await loadNotifications()
      throw err
    }
  }, [getAuthToken, loadNotifications])

  // AGC: Mark notification as unread
  const markAsUnread = useCallback(async (id: string) => {
    try {
      const token = await getAuthToken()
      await updateNotification(id, { read: false }, token)
      
      // AGC: Update local state immediately
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: false } : n)
      )
    } catch (err) {
      console.error('AGC: Error marking notification as unread:', err)
      await loadNotifications()
      throw err
    }
  }, [getAuthToken, loadNotifications])

  // AGC: Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const token = await getAuthToken()
      await markAllNotificationsAsRead(token)
      
      // AGC: Update local state immediately
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    } catch (err) {
      console.error('AGC: Error marking all notifications as read:', err)
      await loadNotifications()
      throw err
    }
  }, [getAuthToken, loadNotifications])

  // AGC: Delete notification
  const deleteNotificationById = useCallback(async (id: string) => {
    try {
      const token = await getAuthToken()
      await deleteNotification(id, token)
      
      // AGC: Update local state immediately
      setNotifications(prev => prev.filter(n => n.id !== id))
    } catch (err) {
      console.error('AGC: Error deleting notification:', err)
      await loadNotifications()
      throw err
    }
  }, [getAuthToken, loadNotifications])

  // AGC: Delete all notifications
  const deleteAll = useCallback(async () => {
    try {
      const token = await getAuthToken()
      await deleteAllNotifications(token)
      
      // AGC: Update local state immediately
      setNotifications([])
    } catch (err) {
      console.error('AGC: Error deleting all notifications:', err)
      await loadNotifications()
      throw err
    }
  }, [getAuthToken, loadNotifications])

  return {
    notifications,
    isLoading,
    error,
    unreadCount,        // AGC: Only non-activity unread (for bell badge)
    activityCount,      // AGC: Total activities logged
    notificationCount,  // AGC: Total system notifications
    stats,
    refreshNotifications,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotificationById,
    deleteAll,
  }
}

